import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { Check, Zap, Smartphone, Layers, Star } from "lucide-react";

export default function PackPage() {
  const nav = useNavigate();
  const { addItem, has } = useCart();
  const { data: pack, isLoading } = useQuery({ queryKey: ["product", "pack"], queryFn: async () => (await api.get("/products/pack-collection-complete")).data });

  if (isLoading || !pack) return <div className="min-h-[50vh] flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-terracotta border-t-transparent animate-spin" /></div>;

  const buyNow = () => { addItem(pack); nav("/panier"); };
  const separate = pack.compare_price || 0;
  const economy = separate - pack.price;

  return (
    <div>
      <section className="bg-ink text-sand">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-overline text-terracotta-light mb-3">La collection complète</p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05]">Les 3 eBooks réunis</h1>
            <p className="mt-5 text-sand/75 leading-relaxed">{pack.description}</p>
            <div className="flex items-center gap-4 mt-8">
              <span className="text-4xl font-semibold text-white">{formatPrice(pack.price)}</span>
              {separate > 0 && <span className="text-xl text-sand/50 line-through">{formatPrice(separate)}</span>}
              {economy > 0 && <span className="bg-sage text-white text-sm px-3 py-1 rounded-full">Économisez {formatPrice(economy)}</span>}
            </div>
            <Button size="lg" onClick={buyNow} disabled={has(pack.id)} className="mt-8 bg-terracotta hover:bg-terracotta-dark text-white rounded-md px-10" data-testid="pack-buy">
              {has(pack.id) ? "Déjà dans le panier" : "Acheter le pack"}
            </Button>
          </div>
          <div className="flex items-end justify-center gap-4">
            {pack.pack_items?.map((c, i) => (
              <img key={c.slug} src={c.cover_url} alt={c.title} className={`book-shadow rounded-sm w-1/3 ${i === 1 ? "-mb-6 scale-110 z-10" : ""}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <Reveal><h2 className="font-heading text-3xl text-ink tracking-tight text-center mb-10">Le contenu de la collection</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {pack.pack_items?.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.08} className="bg-cream border border-line rounded-lg p-6">
              <img src={c.cover_url} alt={c.title} className="w-32 mx-auto book-shadow rounded-sm mb-5" />
              <h3 className="font-heading text-xl text-ink text-center">{c.title}</h3>
              <p className="text-sm text-ink-muted mt-2 text-center leading-relaxed">{c.short_description}</p>
              <Link to={`/ebook/${c.slug}`} className="block text-center text-terracotta text-sm mt-4 hover:underline">Découvrir ce guide</Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-cream border-y border-line">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 grid sm:grid-cols-3 gap-8 text-center">
          {[{ i: Layers, t: "Un accompagnement complet", d: "Du développement de l'enfant à son assiette." }, { i: Zap, t: "Accès immédiat", d: "Vos 3 eBooks disponibles dès le paiement." }, { i: Smartphone, t: "Sur tous vos écrans", d: "Ordinateur, tablette et smartphone." }].map((x) => (
            <div key={x.t}>
              <div className="mx-auto h-12 w-12 rounded-full bg-sand border border-line flex items-center justify-center mb-4"><x.i className="h-5 w-5 text-terracotta" /></div>
              <h3 className="font-heading text-lg text-ink">{x.t}</h3>
              <p className="text-sm text-ink-muted mt-2">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
        <div className="flex justify-center gap-1 mb-4">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-5 w-5 fill-terracotta text-terracotta" />)}</div>
        <h2 className="font-heading text-3xl text-ink tracking-tight">Économisez en choisissant la collection</h2>
        <div className="mt-6 inline-flex items-center gap-6 bg-cream border border-line rounded-lg px-8 py-5">
          <div className="text-left"><p className="text-xs font-overline text-ink-muted">Les 3 séparément</p><p className="text-xl text-ink-muted line-through">{formatPrice(separate)}</p></div>
          <div className="text-left"><p className="text-xs font-overline text-terracotta">Le pack</p><p className="text-2xl font-semibold text-ink">{formatPrice(pack.price)}</p></div>
        </div>
        <div><Button size="lg" onClick={buyNow} className="mt-8 bg-terracotta hover:bg-terracotta-dark text-white rounded-md px-10" data-testid="pack-buy-bottom">Acheter le pack — {formatPrice(pack.price)}</Button></div>
      </section>
    </div>
  );
}
